import { useAuth } from "@/context/AuthContext";
import { firestore } from "@/firebase/web";
import { Group } from "@/types/group";
import { isPublicGroup } from "@/utils/groupVisibility";
import {
  collection,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { useEffect, useState } from "react";

export type GroupAccessStatus =
  | "loading"
  | "member"
  | "non-member"
  | "request-pending";

export interface UseGroupAccessResult {
  status: GroupAccessStatus;
  group: Pick<Group, "id" | "name" | "adminId" | "visibility"> | null;
  pendingRequestId?: string;
  canRead: boolean;
  canPost: boolean;
}

export function useGroupAccess(
  groupId: string | undefined | null,
): UseGroupAccessResult {
  const { user } = useAuth();
  const [group, setGroup] = useState<Pick<
    Group,
    "id" | "name" | "adminId" | "visibility"
  > | null>(null);
  const [isMember, setIsMember] = useState<boolean | null>(null);
  const [pendingRequestId, setPendingRequestId] = useState<string | undefined>(
    undefined,
  );
  const [requestChecked, setRequestChecked] = useState(false);

  useEffect(() => {
    if (!groupId || !user) {
      setGroup(null);
      setIsMember(null);
      setPendingRequestId(undefined);
      setRequestChecked(false);
      return;
    }

    const unsubscribeGroup = onSnapshot(
      doc(firestore, "groups", groupId),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as Group;
          setGroup({
            id: snap.id,
            name: data.name,
            adminId: data.adminId,
            visibility: data.visibility,
          });
          setIsMember(
            Array.isArray(data.memberIds) && data.memberIds.includes(user.uid),
          );
        } else {
          setGroup(null);
          setIsMember(false);
        }
      },
    );

    const requestsQuery = query(
      collection(firestore, "groupAccessRequests"),
      where("requesterId", "==", user.uid),
      where("groupId", "==", groupId),
      where("status", "==", "pending"),
    );
    const unsubscribeRequest = onSnapshot(requestsQuery, (snapshot) => {
      const first = snapshot.docs[0];
      setPendingRequestId(first ? first.id : undefined);
      setRequestChecked(true);
    });

    return () => {
      unsubscribeGroup();
      unsubscribeRequest();
    };
  }, [groupId, user]);

  let status: GroupAccessStatus = "loading";
  if (isMember === true) {
    status = "member";
  } else if (isMember === false && requestChecked) {
    status = pendingRequestId ? "request-pending" : "non-member";
  }

  const isPublic = isPublicGroup(group);
  const canRead = status === "member" || (status !== "loading" && isPublic);
  const canPost = status === "member";

  return { status, group, pendingRequestId, canRead, canPost };
}
